import { CallStatusStore } from '@ngrx-helpers/call-status';
import { lastValueFrom } from 'rxjs';
import { waitForFalse } from '@ngrx-helpers/wait';
import { isNotNullish } from '@ts-helpers/nullish';

export async function loadOperationAsync<
  State extends CallStatusStore<string | number>,
  Result,
>(config: {
  state: State;
  key?: State extends CallStatusStore<infer ID> ? ID : undefined;
  load: (state: State, key?: State extends CallStatusStore<infer ID> ? ID : undefined) => void;
  result: (state: State) => Result;
  exists?: (
    state: State,
    key?: State extends CallStatusStore<infer ID> ? ID : undefined
  ) => boolean;
  force?: boolean;
}): Promise<Result> {
  const { state, key, load, result, force } = config;
  const exists =
    config.exists ||
    (s => {
      const output = result(s);
      return Array.isArray(output) ? output.length > 0 : isNotNullish(output);
    });

  load(state, key);

  if (!force && exists(state, key)) {
    return result(state);
  }

  const isLoadingSignal = key ? state.isLoading(key) : state.isLoading();
  await lastValueFrom(waitForFalse(isLoadingSignal));

  const error = state.loadingError()();
  if (error) throw error;

  return result(state);
}

export async function createOperationAsync<
  State extends CallStatusStore<string | number>,
  Result,
>(config: {
  state: State;
  tempId: string;
  create: (state: State, tempId: string) => void;
  result: (state: State, id: string | number) => Result;
}): Promise<Result> {
  const { state, tempId, create, result } = config;

  create(state, tempId);

  await lastValueFrom(waitForFalse(state.isCreating(tempId)));

  const error = state.creatingError(tempId)();
  if (error) throw error;

  const id = state.created(tempId)();
  if (!id) throw new Error(`Not found created entity with tempId ${tempId}`);

  return result(state, id);
}

export async function updateOperationAsync<
  State extends CallStatusStore<string | number>,
  Result,
>(config: {
  state: State;
  key?: State extends CallStatusStore<infer ID> ? ID : undefined;
  update: (state: State, key?: State extends CallStatusStore<infer ID> ? ID : undefined) => void;
  result: (state: State) => Result;
}): Promise<Result> {
  const { state, key, update, result } = config;

  update(state, key);

  const isUpdatingSignal = key ? state.isUpdating(key) : state.isUpdating();
  await lastValueFrom(waitForFalse(isUpdatingSignal));

  const error = state.updatingError(key)();
  if (error) throw error;

  return result(state);
}

export async function deleteOperationAsync<State extends CallStatusStore<string | number>>(config: {
  state: State;
  key?: State extends CallStatusStore<infer ID> ? ID : undefined;
  remove: (state: State, key?: State extends CallStatusStore<infer ID> ? ID : undefined) => void;
}): Promise<void> {
  const { state, key, remove } = config;

  remove(state, key);

  const isDeletingSignal = key ? state.isDeleting(key) : state.isDeleting();
  await lastValueFrom(waitForFalse(isDeletingSignal));

  const error = state.deletingError(key)();
  if (error) throw error;
}
